/* 
Common code for the levy collection provider calculation components.
*/
import _ from "underscore";
import { getBlankRecord } from "@/store/levy-collection-overrides/helpers";

const DO_NOT_EXEMPT = "NotExempt";
const EXEMPT = "Exempt";
const OVERRIDE_AMOUNT = "OverrideAmount";

export default {
  props: {
    levyCollection: {
      required: true,
      type: Object
    },
    levyCollectionProvider: {
      required: true,
      type: Object
    },
    estimatorKey: {
      validator: prop => typeof prop === "string" || prop === null,
      default: null
    },
    mode: {
      type: String,
      validator: prop => prop == "levy-estimator" || prop == "public-levy-estimator" || prop == "internal",
      required: true
    }
  },
  data() {
    // if altering these, make sure you keep them consistent with the computed "panels()"
    // property on LevyCalculationPanel.vue
    return {
      panelAdministrativeFeeBreakdown: 1,
      panelRiskRatedPremiumBreakdown: 2,
      panelFinancialStrengthScore: 3,
      panelNetProfitRatioScore: 4,
      panelReturnOnAssetsScore: 5,
      panelDebtToEquityScore: 6,
      panelCourseCompletionScore: 7,
      panelNonComplianceScore: 8,
      panelLatePaymentScore: 9,
      panelShortRegistrationScore: 10,
      panelSpecialTuitionProtectionBreakdown: 11,
      panelLevyCap: 12,
      panelParameters: 13
    };
  },
  computed: {
    isLevyEstimator() {
      return this.mode == "levy-estimator";
    },
    isPublicLevyEstimator() {
      return this.mode == "public-levy-estimator";
    },
    isInternal() {
      return this.mode == "internal";
    },
    playgroundIsActive() {
      return this.isLevyEstimator && this.$store.state.levyEstimator.playground.active;
    },
    levyCalculation() {
      return this.levyCollectionProvider.levyCalculation;
    },
    levyParameters() {
      return this.levyCollectionProvider.levyParameters;
    },
    calculationOverride() {
      const override = _(this.levyCollectionProvider.overrides).find(o => o.category == "AdjustLevyCalculations");
      return override ? override : getBlankRecord();
    },
    state() {
      return this.isPublicLevyEstimator
        ? this.$store.state.levyEstimator.publicEstimator
        : this.$store.state.levyEstimator.estimator;
    },
    templates() {
      return this.state.data.indexedTemplates;
    },
    levyCollectionTypeDescription() {
      return this.isVsl ? "VSL" : this.isHelp ? "HELP" : this.isUpfront ? "Up-front" : "UNKNOWN";
    },
    loanTypeDescription() {
      return this.isUpfront ? "payments" : "loans";
    },
    totalLoanTypeDescription() {
      return this.isUpfront ? "Total Up-front payments" : "Total student loans";
    },
    levyCollectionYear() {
      return this.levyCollection.year ? this.levyCollection.year : null;
    },
    isVsl() {
      return this.levyCollection.type ? this.levyCollection.type.toLowerCase().startsWith("vsl") : false;
    },
    isHelp() {
      return this.levyCollection.type ? this.levyCollection.type.toLowerCase().startsWith("help") : false;
    },
    isUpfront() {
      return this.levyCollection.type ? this.levyCollection.type.toLowerCase().startsWith("upfront") : false;
    },
    hasTotal() {
      return this.levyCalculation && this.levyCalculation.totalLevy != null;
    },
    totalRiskScore() {
      const lc = this.levyCalculation;
      return lc.financialStrengthScore + lc.courseCompletionScore + lc.nonComplianceScore;
    },
    administrationFeeExempt() {
      const type = this.calculationOverride.administrativeFeeOverrideType;
      if (type == OVERRIDE_AMOUNT) {
        return false;
      }
      return type == EXEMPT;
    },
    administrationFeeWarning() {
      return this.getOverrideTypeDescription(
        this.calculationOverride.administrativeFeeOverrideType,
        this.administrationFeeExempt
      );
    },
    riskRatingExempt() {
      const type = this.calculationOverride.riskPremiumOverrideType;
      if (type == OVERRIDE_AMOUNT) {
        return false;
      }
      return this.levyParameters.isNew || type == EXEMPT;
    },
    riskRatingWarning() {
      return this.getOverrideTypeDescription(this.calculationOverride.riskPremiumOverrideType, this.riskRatingExempt);
    },
    specialTuitionProtectionExempt() {
      const type = this.calculationOverride.specialTuitionProtectionOverrideType;
      if (type == OVERRIDE_AMOUNT) {
        return false;
      }
      return this.levyParameters.isNew || type == EXEMPT;
    },
    specialTuitionProtectionWarning() {
      return this.getOverrideTypeDescription(
        this.calculationOverride.specialTuitionProtectionOverrideType,
        this.specialTuitionProtectionExempt
      );
    },
    administrativeFeeAmountOverridden() {
      return this.calculationOverride.administrativeFeeOverrideType == OVERRIDE_AMOUNT;
    },
    riskPremiumAmountsOverridden() {
      return this.calculationOverride.riskPremiumOverrideType == OVERRIDE_AMOUNT;
    },
    specialTuitionProtectionAmountsOverridden() {
      return this.calculationOverride.specialTuitionProtectionOverrideType == OVERRIDE_AMOUNT;
    },
    showScores() {
      return !this.riskRatingExempt && !this.riskPremiumAmountsOverridden;
    },
    showFinancialStrengthSubScores() {
      return this.showScores && this.levyParameters.financialStatementSupplied != false;
    },
    showNonComplianceHistorySubScores() {
      return this.showScores && this.levyCollection.enableShortRegistrationPeriodRiskFactor;
    },
    hasLevyCap() {
      return this.levyCalculation.levyCap != null;
    }
  },
  methods: {
    getOverrideTypeDescription(type, isExempt) {
      if (type == EXEMPT) {
        return "exempted by override";
      } else if (type == DO_NOT_EXEMPT) {
        return "exemption overridden";
      } else if (type == OVERRIDE_AMOUNT) {
        return "amount overridden";
      } else if (isExempt) {
        return "exempt";
      }
      return null;
    }
  }
};
